import { BaseMapper } from '@ps/utils';
import { Option } from '@sapphire/result';

import {
  AnswerOption,
  DataQuality,
  Dependency,
  DynamicProfiler,
  Localization,
  Segmentation,
} from '../../models/localization.model';
import {
  AnswerOptionDto,
  DataQualityDto,
  DependencyDto,
  DynamicProfilerDto,
  LocalizationDto,
  SegmentationDto,
} from '../dtos/localization.dto';

const DEFAULT_DYNAMIC_PROFILER: DynamicProfiler = {
  enable: false,
  priority: 0,
  enableByHt: null,
  activeSurveyCount: null,
};

export class LocalizationMapper extends BaseMapper<
  LocalizationDto,
  Localization
> {
  public fromDto(dto: LocalizationDto): Localization {
    const isEnglish = this._isEnglishLocale(dto.locale);

    return {
      locale: dto.locale,
      text: dto.text,
      placeholder: Option.from(dto.placeholder).unwrapOr(''),
      totalAnswered: Option.from(dto.stats).mapOr(0, (stats) => stats.total),
      hasDependency: dto.hasDependency,
      dependency: Option.from(dto.dependency).mapOr(null, (dependency) =>
        this._mapDependency(dependency),
      ),
      hasSegmentation: dto.hasSegmentation,
      segmentation: Option.from(dto.segmentation).mapOr(null, (segmentation) =>
        this._mapSegmentation(segmentation),
      ),
      dataQuality: Option.from(dto.data_quality).mapOr(null, (dataQuality) =>
        this._mapDataQuality(dataQuality),
      ),
      dynamicProfiler: Option.from(dto.dynamic_profiler).mapOr(
        DEFAULT_DYNAMIC_PROFILER,
        (dynamicProfiler) => this._mapDynamicProfiler(dynamicProfiler),
      ),
      nonGdpr: Option.from(dto.non_gdpr).unwrapOr(false),
      ppText: Option.from(dto.pp_text).unwrapOr(''),
      ppModalText: Option.from(dto.pp_modal_text).unwrapOr(''),
      createSurveyPosition: Option.from(dto.create_survey_position).unwrapOr(0),
      createSurveyComponent: Option.from(dto.create_survey_component).unwrapOr(
        0,
      ),
      nameAlias: Option.from(dto.name_alias).unwrapOr(''),
      hasCensusData: Option.from(dto.has_census_data).unwrapOr(false),
      answers: Option.from(dto.answers)
        .unwrapOr([])
        .map((answer) => this._mapAnswer(answer, isEnglish)),
    };
  }

  private _isEnglishLocale(locale: string): boolean {
    return locale.split('_')[0] === 'en';
  }

  private _mapDependency(dto: DependencyDto): Dependency {
    return {
      parentQualificationId: dto.parent_qualification_id,
      matchCriteria: dto.match_criteria,
      condition: dto.condition,
    };
  }

  private _mapSegmentation(dto: SegmentationDto): Segmentation {
    return {
      parentQualificationId: dto.parent_qualification_id,
      childQualificationId: dto.child_qualification_id,
      condition: dto.condition,
    };
  }

  private _mapDataQuality(dto: DataQualityDto): DataQuality {
    return {
      enable: dto.enable,
      eligibleForOeTermination: dto.eligible_for_oe_termination,
      qualityRules: dto.quality_rules,
    };
  }

  private _mapDynamicProfiler(dto: DynamicProfilerDto): DynamicProfiler {
    return {
      enable: dto.enable,
      priority: dto.priority,
      enableByHt: Option.from(dto.enable_by_HT).unwrapOr(null),
      activeSurveyCount: Option.from(dto.active_survey_count).unwrapOr(null),
    };
  }

  private _mapAnswer(dto: AnswerOptionDto, isEnglish: boolean): AnswerOption {
    return {
      id: dto.condition_code,
      text: Option.from(dto.local_text).unwrapOr(''),
      translation: isEnglish ? '' : dto.eng_text,
    };
  }
}
